import { useMemo, useState } from "react";
import { Badge } from "flowbite-react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { formatToReal } from "../../../utils/utils";

const PendenciasFinanceiras = ({
  pendenciasFinanceiras = [],
  quantidadeTotal,
  dataMaiorPendencia,
  valorMaior,
  valorTotal,
  temDataDisponibilidade = true,
  temContrato = true,
}) => {
  const [expandido, setExpandido] = useState(false);

  const hasContrato = pendenciasFinanceiras.some((p) => {
    const contrato = p?.contrato;
    if (contrato === null || contrato === undefined) return false;
    const contratoNormalizado = String(contrato).trim();
    return contratoNormalizado !== "" && contratoNormalizado !== "-";
  });
  const hasCidade = pendenciasFinanceiras.some((p) => {
    const cidade = p?.cidade;
    if (cidade === null || cidade === undefined) return false;
    const cidadeNormalizada = String(cidade).trim();
    return cidadeNormalizada !== "" && cidadeNormalizada !== "-";
  });

  const showContrato = temContrato && hasContrato;
  const showCidade = hasCidade;

  const colunas = [
    { key: "data", template: "minmax(92px,0.9fr)" },
    ...(temDataDisponibilidade
      ? [{ key: "disponibilidade", template: "minmax(92px,0.9fr)" }]
      : []),
    {
      key: "informante",
      template:
        showCidade || showContrato
          ? "minmax(220px,2fr)"
          : "minmax(280px,2.6fr)",
    },
    ...(showCidade ? [{ key: "cidade", template: "minmax(120px,1fr)" }] : []),
    { key: "tipo", template: "minmax(120px,1fr)" },
    ...(showContrato
      ? [{ key: "contrato", template: "minmax(120px,1fr)" }]
      : []),
    { key: "valor", template: "minmax(120px,0.9fr)" },
  ];

  const gridTemplateColumns = colunas
    .map((coluna) => coluna.template)
    .join(" ");

  const LIMITE = 12;
  const total = pendenciasFinanceiras.length;
  const restantes = Math.max(total - LIMITE, 0);

  const listaExibida = useMemo(
    () =>
      expandido
        ? pendenciasFinanceiras
        : pendenciasFinanceiras.slice(0, LIMITE),
    [expandido, pendenciasFinanceiras],
  );

  let classGridResumo = 3;

  if (dataMaiorPendencia && valorMaior) classGridResumo += 1;

  return (
    <>
      {/* Resumo */}
      <div className={`grid gap-3 mb-5 grid-cols-${classGridResumo}`}>
        {quantidadeTotal && (
          <div className="flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-4 py-1 text-sm shadow-sm">
            <div
              className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500"
              style={{ lineHeight: "1.4" }}
            >
              Quantidade total
            </div>
            <div className="text-lg font-semibold text-slate-900">
              {quantidadeTotal}
            </div>
          </div>
        )}

        {dataMaiorPendencia && (
          <div className="flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-4 py-1 text-sm shadow-sm">
            <div
              className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500"
              style={{ lineHeight: "1.4" }}
            >
              Data maior
            </div>
            <div className="text-sm font-semibold text-slate-900">
              {dataMaiorPendencia}
            </div>
          </div>
        )}

        {valorMaior && (
          <div className="flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-4 py-1 text-sm shadow-sm">
            <div
              className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500"
              style={{ lineHeight: "1.4" }}
            >
              Valor maior
            </div>
            <div className="text-sm font-semibold text-slate-900">
              {formatToReal(valorMaior)}
            </div>
          </div>
        )}

        {valorTotal && (
          <div className="flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-4 py-1 text-sm shadow-sm">
            <div
              className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500"
              style={{ lineHeight: "1.4" }}
            >
              Valor total
            </div>
            <div className="text-lg font-semibold text-rose-600">
              {formatToReal(valorTotal)}
            </div>
          </div>
        )}
      </div>

      {/* Tabela + Fade quando colapsado */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div
            className="grid gap-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500"
            style={{ gridTemplateColumns }}
          >
            <div className="text-left">Data</div>
            {temDataDisponibilidade && (
              <div className="text-left">Disponib.</div>
            )}

            <div className="text-left">Informante</div>
            {showCidade && <div className="text-left">Cidade</div>}
            <div className="text-left">Tipo</div>
            {showContrato && <div className="text-left">Contrato</div>}
            <div className="text-right">Valor</div>
          </div>
        </div>

        {/* Área rolável quando colapsado, expandida quando aberto */}
        <div
          className={`transition-all duration-300 ease-out ${
            expandido ? "max-h-[9999px]" : "max-h-auto"
          } overflow-hidden`}
        >
          <div className="divide-y divide-slate-100">
            {listaExibida.map((pendencia, index) => (
              <div
                key={index}
                className={`grid items-center gap-2 px-4 py-3 text-sm transition-colors ${
                  index % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                } hover:bg-slate-50`}
                style={{ gridTemplateColumns }}
              >
                <div className="font-medium text-slate-700">
                  {pendencia?.data || pendencia?.data_debito || "-"}
                </div>
                {temDataDisponibilidade && (
                  <div className="text-slate-600">
                    {pendencia?.dataDisponibilidade || pendencia?.data || "-"}
                  </div>
                )}

                <div
                  className="font-medium text-slate-900 truncate text-xs uppercase"
                  title={pendencia?.informante || ""}
                >
                  {pendencia?.informante || "-"}
                </div>

                {showCidade && (
                  <div className="text-slate-700">
                    {pendencia?.cidade || "-"}
                  </div>
                )}
                <div className="text-slate-700">
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600">
                    {pendencia?.tipoPendencia || pendencia?.tipo || "—"}
                  </span>
                </div>
                {showContrato && (
                  <div className="min-w-0 text-slate-700">
                    <span
                      className="block max-w-full truncate rounded-md border border-slate-200 bg-slate-50 px-1.5 text-[11px] font-medium text-slate-700"
                      title={pendencia?.contrato || ""}
                    >
                      {pendencia?.contrato || "-"}
                    </span>
                  </div>
                )}
                <div className="text-right font-semibold text-rose-600 tabular-nums">
                  {formatToReal(pendencia?.valor)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Fade de “tem mais” quando colapsado */}
        {!expandido && total > LIMITE && (
          <div className="pointer-events-none absolute inset-x-0 bottom-[52px] h-16 bg-gradient-to-t from-white to-white/0" />
        )}

        {/* Footer elegante */}
        {total > LIMITE && (
          <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-3">
            <button
              type="button"
              onClick={() => setExpandido((v) => !v)}
              aria-expanded={expandido}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              {expandido ? (
                <>
                  <HiChevronUp className="text-lg" />
                  Ver menos
                </>
              ) : (
                <>
                  <HiChevronDown className="text-lg" />
                  Ver mais
                </>
              )}
            </button>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Badge color="gray">
                Exibindo {Math.min(LIMITE, total)} de {total}
              </Badge>
              {!expandido && restantes > 0 && (
                <span className="text-slate-500">(+{restantes} restantes)</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Aviso quando colapsado */}
      {!expandido && total > LIMITE && (
        <div className="text-sm mt-3 text-gray-500 text-center">
          Exibindo as últimas {LIMITE} ocorrências.
        </div>
      )}
    </>
  );
};

export default PendenciasFinanceiras;
